// Considered: the editor's former single-purpose weight/coverage fields — rejected because neither said when it
// applied; each guardrail row here names its state and reason. See spec Reuse Audit.
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { BELOW_COVERAGE_STATUSES } from '@center-platform/spheres/rollup';
import type { AggregationMethod, RollupWeighting, RosterCoverage, StatusValue } from 'types/spherePoints';
import DropdownAtom, { type DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import { InputNumberAtom } from '../../atoms/InputNumber/InputNumberAtom';
import { getLocalizedStatusLabel } from '../../../utils/statusColors';
import { fromStored } from './combineModel';
import { diagnosticSentences } from './diagnosticSentences';
import RollupStatusOptionLabel from './RollupStatusOptionLabel';
import './RollupGuardrailsSection.scss';

export interface CoverageFloorValue {
  percent: number;
  status: StatusValue;
  /** The stored fraction this floor was loaded from; kept while the percent is unedited so a save never rounds it. */
  exact?: number;
}

export interface RollupGuardrailsSectionProps {
  aggregationMethod: AggregationMethod;
  weighting: RollupWeighting;
  influenceFloor: number | null;
  coverageFloor: CoverageFloorValue | null;
  /** The roster's live coverage; absent until the roster has resolved. */
  coverage?: Pick<RosterCoverage, 'reporting' | 'total'>;
  /** Members whose influence is editable on an edge; the weight floor only means something when this is above zero. */
  edgeBackedCount?: number;
  /** Diagnostic codes of the stored config; the caller passes none while the form has unsaved changes. */
  diagnostics?: string[];
  contributionUnavailableCount?: number;
  onChange: (updates: { influenceFloor?: number | null; coverageFloor?: CoverageFloorValue | null }) => void;
  disabled?: boolean;
  className?: string;
}

const DEFAULT_COVERAGE_PERCENT = 80;
// Unknown leads: it is the safest answer for a thin population.
const STATUS_ORDER: StatusValue[] = ['unknown', ...BELOW_COVERAGE_STATUSES.filter((s) => s !== 'unknown')];

const isValidPercent = (value: number | null): value is number =>
  value !== null && Number.isInteger(value) && value >= 1 && value <= 99;

const RollupGuardrailsSection: React.FC<RollupGuardrailsSectionProps> = ({
  aggregationMethod,
  weighting,
  influenceFloor,
  coverageFloor,
  coverage,
  edgeBackedCount = 0,
  diagnostics,
  contributionUnavailableCount,
  onChange,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const uid = useId();
  const axes = fromStored({ aggregationMethod, weighting });
  const pendingPctFocus = useRef(false);
  const [pctDraft, setPctDraft] = useState<number | null>(coverageFloor?.percent ?? null);
  const [pctTouched, setPctTouched] = useState(false);

  useEffect(() => {
    if (coverageFloor) setPctDraft(coverageFloor.percent);
  }, [coverageFloor]);

  const hasFloor = coverageFloor !== null;
  useEffect(() => {
    if (!pendingPctFocus.current) return;
    pendingPctFocus.current = false;
    document.getElementById(`${uid}-coverage-pct`)?.focus();
  }, [hasFloor, uid]);

  const weightFloorActive = axes.wins === 'worst' && axes.weighting === 'influence' && edgeBackedCount > 0;
  const showWeightFloor = axes.wins === 'worst';
  let weightNote: string;
  if (axes.weighting !== 'influence') {
    weightNote = t(
      'statusFactors.guardrails.weightFloor.reasonEqual',
      'Inactive while members count equally — switch How much each member counts to By influence to apply it.'
    );
  } else if (edgeBackedCount <= 0) {
    weightNote = t(
      'statusFactors.guardrails.weightFloor.reasonNoEdges',
      'No member carries its own weight yet, so there is nothing to compare against.'
    );
  } else if (influenceFloor === null) {
    weightNote = t('statusFactors.guardrails.weightFloor.stateOff', 'No floor — every member counts toward the worst.');
  } else {
    weightNote = t(
      'statusFactors.guardrails.weightFloor.stateOn',
      'Members lighter than {{floor}} are ignored when finding the worst.',
      { floor: influenceFloor }
    );
  }

  const statusLabel = (status: StatusValue) => getLocalizedStatusLabel(status, t as (key: string) => string);
  const statusOptions = STATUS_ORDER.map((value) => ({ value, label: statusLabel(value) }));

  const percentOf = (c: Pick<RosterCoverage, 'reporting' | 'total'>) =>
    c.total > 0 ? Math.round((c.reporting / c.total) * 100) : 0;

  const pctInvalid = (pctTouched || coverageFloor?.exact !== undefined) && !isValidPercent(pctDraft);
  const pctErrorId = `${uid}-coverage-pct-error`;

  const handlePctChange = (value: number | null) => {
    setPctDraft(value);
    if (isValidPercent(value) && coverageFloor) onChange({ coverageFloor: { percent: value, status: coverageFloor.status } });
  };

  const handleSwitch = (on: boolean) => {
    setPctTouched(false);
    if (on) {
      pendingPctFocus.current = true;
      setPctDraft(DEFAULT_COVERAGE_PERCENT);
      onChange({ coverageFloor: { percent: DEFAULT_COVERAGE_PERCENT, status: 'unknown' } });
    } else {
      onChange({ coverageFloor: null });
    }
  };

  let coverageNote: string | null = null;
  let coverageNoteActive = false;
  if (coverageFloor) {
    if (!coverage) {
      coverageNote = t(
        'statusFactors.guardrails.coverageFloor.unavailable',
        'Coverage is unavailable right now.'
      );
    } else if (coverage.total <= 0) {
      coverageNote = t(
        'statusFactors.guardrails.coverageFloor.noRoster',
        'Coverage is measured once this factor has members.'
      );
    } else {
      const values = {
        reporting: coverage.reporting,
        total: coverage.total,
        pct: percentOf(coverage),
        floor: coverageFloor.percent,
        status: statusLabel(coverageFloor.status),
      };
      coverageNoteActive = coverage.reporting / coverage.total < coverageFloor.percent / 100;
      coverageNote = coverageNoteActive
        ? t(
            'statusFactors.guardrails.coverageFloor.firing',
            'Active right now — only {{reporting}} of {{total}} members are reporting ({{pct}}%), below the {{floor}}% you set, so this factor shows as {{status}}.',
            values
          )
        : t(
            'statusFactors.guardrails.coverageFloor.notFiring',
            "{{reporting}} of {{total}} members are reporting ({{pct}}%), so the floor isn't active.",
            values
          );
    }
  }

  const classes = ['rollup-guardrails-section', className].filter(Boolean).join(' ');
  const weightLabelId = `${uid}-weight-floor-label`;
  const coverageLabelId = `${uid}-coverage-floor-label`;
  const onLabel = t('statusFactors.guardrails.coverageFloor.labelOn', 'Coverage floor — on');

  const storedSentences = diagnosticSentences(
    diagnostics,
    {
      reporting: coverage?.reporting,
      total: coverage?.total,
      minCoverage: coverageFloor?.percent,
      floorStatusLabel: coverageFloor ? getLocalizedStatusLabel(coverageFloor.status, t as (key: string) => string) : undefined,
      influenceFloor: influenceFloor === null ? undefined : String(influenceFloor),
      contributionUnavailableCount,
    },
    t
  );

  return (
    <section className={classes} aria-labelledby={`${uid}-heading`} data-testid="rollup-guardrails-section">
      <h3 id={`${uid}-heading`} className="rollup-guardrails-section__heading">
        {t('statusFactors.guardrails.heading', 'Trust guardrails')}
      </h3>

      {showWeightFloor && (
        <div
          className="rollup-guardrails-section__row rollup-guardrails-section__row--split"
          data-testid="rollup-weight-floor"
          data-active={weightFloorActive}
        >
          <div className="rollup-guardrails-section__text">
            <span id={weightLabelId} className="rollup-guardrails-section__label">
              {t(
                'statusFactors.guardrails.weightFloor.label',
                'Weight floor — ignore members lighter than this when flagging the worst'
              )}
            </span>
            <p className="rollup-guardrails-section__note" data-testid="rollup-weight-floor-note">
              {weightNote}
            </p>
          </div>
          <InputNumberAtom
            size="compact"
            inputId={`${uid}-weight-floor`}
            ariaLabelledBy={weightLabelId}
            value={influenceFloor}
            onChange={(value) => onChange({ influenceFloor: value })}
            className="rollup-guardrails-section__number"
            min={0}
            precision={2}
            disabled={disabled || !weightFloorActive}
          />
        </div>
      )}

      <div className="rollup-guardrails-section__row" data-testid="rollup-coverage-floor">
        <div className="rollup-guardrails-section__switch">
          <SwitchAtom
            size="compact"
            inputId={`${uid}-coverage-switch`}
            aria-labelledby={coverageFloor ? undefined : coverageLabelId}
            aria-label={coverageFloor ? onLabel : undefined}
            checked={coverageFloor !== null}
            onChange={handleSwitch}
            disabled={disabled}
          />
        </div>
        <div className="rollup-guardrails-section__coverage-body">
          {coverageFloor ? (
            <>
            <div
              className="rollup-guardrails-section__controls rollup-guardrails-section__sentence"
              data-testid="rollup-coverage-floor-sentence"
            >
              <span>{t('statusFactors.guardrails.coverageFloor.before', 'If fewer than')}</span>
              <span
                className="rollup-guardrails-section__pct"
                onBlur={() => setPctTouched(true)}
              >
                <InputNumberAtom
                  size="compact"
                  inputId={`${uid}-coverage-pct`}
                  ariaLabel={t('statusFactors.guardrails.coverageFloor.pctLabel', 'Coverage floor percentage')}
                  ariaDescribedBy={pctInvalid ? pctErrorId : undefined}
                  className="rollup-guardrails-section__number rollup-guardrails-section__number--pct"
                  value={pctDraft}
                  onChange={handlePctChange}
                  type="integer"
                  suffix="%"
                  invalid={pctInvalid}
                  disabled={disabled}
                />
              </span>
              <span>{t('statusFactors.guardrails.coverageFloor.middle', 'of members are reporting, show this factor as')}</span>
              <DropdownAtom
                dropdownSize="compact"
                inputId={`${uid}-coverage-status`}
                className="rollup-guardrails-section__status"
                aria-label={t('statusFactors.guardrails.coverageFloor.statusLabel', 'Status to show below the coverage floor')}
                value={coverageFloor.status}
                onChange={(e: DropdownChangeEvent) =>
                  onChange({ coverageFloor: { ...coverageFloor, status: e.value as StatusValue } })
                }
                options={statusOptions}
                optionLabel="label"
                optionValue="value"
                valueTemplate={(option: { value: StatusValue } | null) =>
                  option ? <RollupStatusOptionLabel status={option.value} /> : null
                }
                itemTemplate={(option: { value: StatusValue }) => <RollupStatusOptionLabel status={option.value} />}
                disabled={disabled}
              />
              <span>{t('statusFactors.guardrails.coverageFloor.after', 'instead.')}</span>
            </div>
            {pctInvalid && (
              <p id={pctErrorId} className="rollup-guardrails-section__error" data-testid="rollup-coverage-pct-error">
                {t('statusFactors.guardrails.coverageFloor.pctError', 'Enter a percentage between 1 and 99.')}
              </p>
            )}
            <p
              className={`rollup-guardrails-section__note${coverageNoteActive ? ' rollup-guardrails-section__note--active' : ''}`}
              data-testid="rollup-coverage-note"
            >
              {coverageNote}
            </p>
            </>
          ) : (
            <>
              <span id={coverageLabelId} className="rollup-guardrails-section__label">
                {t('statusFactors.guardrails.coverageFloor.labelOff', 'Coverage floor — off')}
              </span>
              <p className="rollup-guardrails-section__note" data-testid="rollup-coverage-live">
                {t(
                  'statusFactors.guardrails.coverageFloor.offHint',
                  'Turn on to distrust the blend when too few members are reporting.'
                )}
                {coverage && coverage.total > 0 && (
                  <>
                    {' '}
                    {t(
                      'statusFactors.guardrails.coverageFloor.off',
                      '{{reporting}} of {{total}} members reporting ({{pct}}%) — no minimum is enforced.',
                      { reporting: coverage.reporting, total: coverage.total, pct: percentOf(coverage) }
                    )}
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
      {storedSentences.length > 0 && (
        <ul className="rollup-guardrails-section__diagnostics" data-testid="rollup-guardrails-diagnostics">
          {storedSentences.map((sentence) => (
            <li key={sentence.code}>{sentence.text}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

RollupGuardrailsSection.displayName = 'RollupGuardrailsSection';

export default RollupGuardrailsSection;
